import React, { useState, useCallback, useEffect } from 'react';
import { Box, Typography, TextField, IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import { EmailSection } from '../../types/email';

interface EditableEmailSectionProps {
  section: EmailSection;
  onContentChange: (sectionId: string, newContent: string) => void;
}

export const EditableEmailSection: React.FC<EditableEmailSectionProps> =
  React.memo(({ section, onContentChange }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editContent, setEditContent] = useState(
      section.content || section.defaultContent || '',
    );

    // Update local state when section content changes
    useEffect(() => {
      setEditContent(section.content || section.defaultContent || '');
    }, [section.content, section.defaultContent]);

    const handleSave = useCallback(() => {
      onContentChange(section.id, editContent);
      setIsEditing(false);
    }, [editContent, onContentChange, section.id]);

    const handleCancel = useCallback(() => {
      setEditContent(section.content || section.defaultContent || '');
      setIsEditing(false);
    }, [section.content, section.defaultContent]);

    const handleContentChange = useCallback(
      (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setEditContent(event.target.value);
      },
      [],
    );

    const hasContent = Boolean(section.content || section.defaultContent);

    return (
      <Box
        sx={{
          marginBottom: 2,
          padding: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          backgroundColor: 'background.paper',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 1,
          }}
        >
          <Typography variant="subtitle1" component="h3">
            {section.name}
            {section.required && (
              <Typography component="span" color="error" sx={{ marginLeft: 1 }}>
                *
              </Typography>
            )}
          </Typography>
          {!isEditing ? (
            <Tooltip title="Edit section">
              <IconButton onClick={() => setIsEditing(true)} size="small">
                <EditIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Box>
              <Tooltip title="Save changes">
                <IconButton onClick={handleSave} size="small" color="primary">
                  <CheckIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cancel">
                <IconButton onClick={handleCancel} size="small" color="error">
                  <CancelIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
        {isEditing ? (
          <TextField
            fullWidth
            multiline
            minRows={3}
            value={editContent}
            onChange={handleContentChange}
            variant="outlined"
            placeholder="Enter content here..."
          />
        ) : (
          <Typography
            variant="body1"
            sx={{
              whiteSpace: 'pre-wrap',
              color: hasContent ? 'text.primary' : 'text.secondary',
            }}
          >
            {hasContent ? editContent : 'No content yet'}
          </Typography>
        )}
      </Box>
    );
  });

EditableEmailSection.displayName = 'EditableEmailSection';
